import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';

/**
 * Confirms removing one connection.
 *
 * Says what disappears — the managed entries and the trigger profiles left
 * pointing at nothing — without blocking: the backend applies no reference guard
 * (FR-006). Purely presentational; the page owns which connection is pending,
 * what confirming does, and — when the service refuses — the reason shown
 * inside it.
 */
export function DeleteConnectionDialog({
  connectionName,
  open,
  isPending,
  error,
  onConfirm,
  onCancel,
  exposedEnv,
  triggerProfileCount,
  verb = 'delete',
}: {
  connectionName: string;
  open: boolean;
  isPending: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** A catalog connection's managed entries, deleted with it. Absent for a
   *  connection that only references a secret. */
  exposedEnv?: string[];
  triggerProfileCount?: number;
  /** "disconnect" for a catalog connection, so the dialog matches the button
   *  that opened it. */
  verb?: 'delete' | 'disconnect';
}) {
  const Verb = verb === 'disconnect' ? 'Disconnect' : 'Delete';
  return (
    <ConfirmDestructiveDialog
      open={open}
      title={
        <>
          {Verb} &ldquo;{connectionName}&rdquo;?
        </>
      }
      confirmLabel={verb === 'disconnect' ? 'Disconnect' : 'Delete connection'}
      isPending={isPending}
      error={error}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {exposedEnv && exposedEnv.length > 0 ? (
        <>
          This removes &ldquo;{connectionName}&rdquo; and the entries it created
          from this workspace:{' '}
          {exposedEnv.map((key, index) => (
            <span key={key}>
              {index > 0 ? ', ' : null}
              <span className="font-mono whitespace-nowrap">{key}</span>
            </span>
          ))}
          . Agents lose access on their next run.
        </>
      ) : (
        <>
          This removes &ldquo;{connectionName}&rdquo; from this workspace. It
          does not delete the secret it references.
        </>
      )}
      {triggerProfileCount && triggerProfileCount > 0
        ? ` ${String(triggerProfileCount)} trigger ${triggerProfileCount === 1 ? 'profile uses' : 'profiles use'} it and will fail their next scheduled run.`
        : ' No trigger profile uses it.'}
    </ConfirmDestructiveDialog>
  );
}
